import { Check, Quote } from "lucide-react";
import { useReveal } from "@/lib/useReveal";

const SPECIALTIES = [
  "Tax foreclosure surplus recovery",
  "Mortgage foreclosure overage recovery",
  "Property sale excess proceeds research",
  "Claim assistance and document guidance",
  "Helping clients navigate complex county processes",
];

export const About = () => {
  const ref = useReveal();
  return (
    <section
      id="about"
      data-testid="about-section"
      ref={ref}
      className="bg-[#FDFBF7] py-24 sm:py-32 relative"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-20">
          {/* Left: founder identity card */}
          <aside className="lg:col-span-5 reveal lg:sticky lg:top-28 self-start">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-[#D4AF37]" />
              <span className="text-[#1E3A5F] text-xs uppercase tracking-[0.3em]">
                Our Founder
              </span>
            </div>

            <div className="bg-[#F3F0E6] border border-[#E2DCD0] p-10 rounded-sm">
              <div className="flex items-center gap-5 mb-8">
                <div
                  aria-hidden
                  className="h-20 w-20 rounded-sm bg-[#0A192F] text-[#D4AF37] flex items-center justify-center font-serif-display text-3xl border border-[#D4AF37]/30"
                >
                  SK
                </div>
                <div>
                  <div className="font-serif-display text-2xl text-[#0A192F] leading-tight">
                    Silas Keys
                  </div>
                  <div className="text-[#1E3A5F] text-sm mt-1">
                    Founder
                  </div>
                  <div className="text-[#1E3A5F] text-xs mt-0.5 uppercase tracking-[0.18em]">
                    Fort Worth, Texas
                  </div>
                </div>
              </div>

              <Quote className="h-6 w-6 text-[#D4AF37] mb-4" />
              <p className="font-serif-display text-[#0A192F] text-2xl leading-snug italic">
                "Your surplus. Your money. Your second chance."
              </p>

              <div className="hairline my-8 opacity-50" />

              <div className="text-[#1E3A5F] text-xs uppercase tracking-[0.22em] mb-4">
                We Specialize In
              </div>
              <ul className="space-y-3">
                {SPECIALTIES.map((s) => (
                  <li
                    key={s}
                    className="flex items-start gap-3 text-sm text-[#0A192F]"
                  >
                    <Check className="h-4 w-4 text-[#D4AF37] mt-0.5 shrink-0" />
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </div>
          </aside>

          {/* Right: narrative */}
          <div
            className="lg:col-span-7 reveal"
            style={{ transitionDelay: "120ms" }}
          >
            <h2 className="font-serif-display text-[#0A192F] text-4xl sm:text-5xl lg:text-6xl font-medium leading-[1.05]">
              Built in Fort Worth.
              <br />
              <em className="italic text-[#1E3A5F]">For Texans, everywhere.</em>
            </h2>

            <div className="mt-10 space-y-6 text-[#1E3A5F] text-lg leading-relaxed">
              <p>
                At Lone Star Surplus Recovery LLC, we help former homeowners and
                families identify and recover surplus funds left behind after
                foreclosure and tax sales across Texas. Many people never realize
                they may still be owed money after a property is sold — and{" "}
                <strong className="text-[#0A192F]">that's where we step in.</strong>
              </p>

              <p>
                Founded by Fort Worth native{" "}
                <strong className="text-[#0A192F]">Silas Keys</strong>, our
                company was built on one mission: bringing{" "}
                <em>awareness, transparency, and real results</em> to
                individuals who may have funds waiting to be claimed. Born and
                raised in Fort Worth, Silas understands the importance of
                community, hard work, and creating opportunities that truly
                change lives.
              </p>

              <p>
                With a strong background in sales, negotiation, and client
                relations, Silas brings persistence, professionalism, and a
                people-first approach to every case. What started as a vision
                to help local families has grown into a dedicated surplus
                recovery operation focused on serving clients throughout Texas.
              </p>

              <p>
                At Lone Star Surplus Recovery LLC, we believe{" "}
                <strong className="text-[#0A192F]">
                  no rightful funds should go unclaimed.
                </strong>{" "}
                Our goal is simple: help everyday people recover what belongs to
                them while providing clear communication and professional
                support every step of the way.
              </p>
            </div>

            {/* Signature */}
            <div className="mt-12 pt-10 border-t border-[#E2DCD0]">
              <div className="font-serif-display text-3xl text-[#0A192F] italic">
                Silas Keys
              </div>
              <div className="text-[#1E3A5F] text-sm mt-2 tracking-wide">
                Founder · Lone Star Surplus Recovery LLC · Fort Worth, Texas
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
